// src/pages/SloughTaxi.jsx
import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import PostcodeQuotenew from "./PostcodeQuotenew";
import LiveStrip from "../components/LiveStrip";
import FloatingSupportChat from "../components/FloatingSupportChat";
import PremiumContactStrip from "../components/PremiumContactStrip";
import CcgPremiumReviews from "../components/CcgPremiumReviews";
import CcgRouteLhrStatusCompact from "../components/CcgRouteLhrStatusCompact";
import "./CcgRoutePremium.css";
import "../styles/slough-taxi.css";

const seoTitle = "Slough Taxi & Fixed-Price Heathrow Transfers | CheapCabGrab";
const seoDescription = "Book reliable Slough taxis for Heathrow, Gatwick, Luton, Stansted and local SL1–SL7 journeys. Fixed-price fares, licensed drivers, MPV & minibus options, 24/7 support.";
const seoCanonical = "https://www.cheapcabgrab.com/slough-taxi";

const sloughTaxiJsonLd = {
  "@context": "https://schema.org",
  "@type": "TaxiService",
  name: "CheapCabGrab – Slough Taxi Service",
  url: seoCanonical,
  areaServed: [
    "Slough SL1",
    "Slough SL2",
    "Slough SL3",
    "Langley",
    "Cippenham",
    "Britwell",
    "Wexham",
    "Chalvey",
  ],
  serviceArea: {
    "@type": "Place",
    name: "Slough and Heathrow corridor",
  },
  sameAs: [
    "https://www.cheapcabgrab.com/",
    "https://www.cheapcabgrab.com/heathrow-taxi",
  ],
};

const sloughFaqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Do you track flights for pickups?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes – for airport journeys we usually track your flight number and adjust pickup time where possible if your flight is delayed. Standard free waiting time is included – full details are shown when you book.",
      },
    },
    {
      "@type": "Question",
      name: "Can I book a child seat?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Child seats can be requested in advance for most Slough journeys. Please include your requirements in the booking notes so we can allocate the right vehicle.",
      },
    },
    {
      "@type": "Question",
      name: "Do you operate late at night?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes, CheapCabGrab operates 24/7 for pre-booked trips. Late-night pickups from Slough, Heathrow and London are available – we recommend booking in advance.",
      },
    },
  ],
};

const SloughTaxi = () => {
  const scrollToForm = () => {
    const el = document.getElementById("slough-quote-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    const setMeta = (selector, attrs) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement("meta");
        document.head.appendChild(element);
      }
      Object.entries(attrs).forEach(([key, value]) => element.setAttribute(key, value));
    };

    const applySeo = () => {
      document.title = seoTitle;

      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", seoCanonical);

      setMeta('meta[name="description"]', { name: "description", content: seoDescription });
      setMeta('meta[name="keywords"]', {
        name: "keywords",
        content: "Slough taxi, SL1 taxi, SL2 taxi, Slough to Heathrow taxi, Heathrow transfers from Slough, fixed price taxi Slough, airport taxi Slough, CheapCabGrab",
      });
      setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
      setMeta('meta[property="og:title"]', {
        property: "og:title",
        content: "Slough Taxi – Fixed-Price Heathrow Transfers & Local SL1–SL7 Cabs",
      });
      setMeta('meta[property="og:description"]', {
        property: "og:description",
        content: "Book a CheapCabGrab taxi from Slough to Heathrow and across SL1–SL7. Fixed fares, no surge pricing, licensed drivers and airport specialists.",
      });
      setMeta('meta[property="og:url"]', { property: "og:url", content: seoCanonical });
      setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
      setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: seoTitle });
      setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: seoDescription });
      setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: "https://www.cheapcabgrab.com/og-image.jpg" });

      const setJsonLd = (id, data) => {
        let script = document.querySelector(`script[data-slough-taxi-jsonld="${id}"]`);
        if (!script) {
          script = document.createElement("script");
          script.type = "application/ld+json";
          script.setAttribute("data-slough-taxi-jsonld", id);
          document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(data);
      };
      setJsonLd("taxi-service", sloughTaxiJsonLd);
      setJsonLd("faq", sloughFaqJsonLd);
    };

    applySeo();
    const timers = [window.setTimeout(applySeo, 0), window.setTimeout(applySeo, 750)];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, []);

  return (
    <div className="slough-page route-page ccg-route-premium ccg-slough-premium">
      <Helmet>
        <title>{seoTitle}</title>
        <meta
          name="description"
          content={seoDescription}
        />
        <link rel="canonical" href={seoCanonical} />
        <meta
          name="keywords"
          content="Slough taxi, SL1 taxi, SL2 taxi, Slough to Heathrow taxi, Heathrow transfers from Slough, fixed price taxi Slough, airport taxi Slough, CheapCabGrab"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Slough Taxi – Fixed-Price Heathrow Transfers & Local SL1–SL7 Cabs"
        />
        <meta
          property="og:description"
          content="Book a CheapCabGrab taxi from Slough to Heathrow and across SL1–SL7. Fixed fares, no surge pricing, licensed drivers and airport specialists."
        />
        <meta
          property="og:url"
          content="https://www.cheapcabgrab.com/slough-taxi"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content="https://www.cheapcabgrab.com/og-image.jpg" />

        {/* JSON-LD – LocalBusiness / TaxiService for Slough */}
        <script type="application/ld+json" data-slough-taxi-jsonld="taxi-service">
          {JSON.stringify(sloughTaxiJsonLd)}
        </script>
        <script type="application/ld+json" data-slough-taxi-jsonld="faq">
          {JSON.stringify(sloughFaqJsonLd)}
        </script>
      </Helmet>

      <LiveStrip variant="slough" />

      <main className="slough-main route-main">
        {/* 🟦 HERO */}
        <section className="ccg-route-hero ccg-slough-hero">
          <div className="ccg-route-hero-shell">
            <div className="ccg-route-hero-top">
              <div className="ccg-route-hero-copy">
                <span className="ccg-route-eyebrow">Slough taxi service</span>
                <h1>Slough Taxi – Heathrow &amp; Local Transfers</h1>
                <p>Fixed-price SL1–SL7 taxis with airport specialists</p>
                <p>
                  Book reliable taxis from Slough, Langley, Cippenham and Britwell to Heathrow,
                  London and nearby towns. Fixed upfront fares, licensed drivers and secure online booking.
                </p>
              </div>

              <div className="ccg-route-trust-cards" aria-label="CheapCabGrab Slough taxi trust signals">
                <article className="ccg-route-trust-card">
                  <b>✓</b>
                  <span>
                    <small>Licensed operator</small>
                    <strong>Licensed &amp; insured</strong>
                    <span>Professional airport transfers</span>
                  </span>
                </article>
                <article className="ccg-route-trust-card">
                  <b>G</b>
                  <span>
                    <small>Google reviews</small>
                    <strong>4.8 ★★★★★</strong>
                    <span>Trusted by local passengers</span>
                  </span>
                </article>
              </div>
            </div>

            <div className="ccg-route-quote-wrap" id="slough-quote-form">
              <div className="ccg-route-quote-head">
                <span className="ccg-route-quote-label">Get instant Slough taxi quotes</span>
                <span className="ccg-route-quote-note"><i /> Free - Fast - No obligation</span>
              </div>
              <PostcodeQuotenew presetPickup="Slough, SL1, UK" />
              <ul className="ccg-route-quote-promises">
                <li>Fixed upfront fare</li>
                <li>Licensed drivers</li>
                <li>Secure online booking</li>
                <li>Instant confirmation</li>
              </ul>
              <PremiumContactStrip />
            </div>

            <ul className="ccg-route-flags">
              <li>Heathrow, Gatwick, Luton &amp; Stansted coverage</li>
              <li>Fixed prices — no surge or meter shocks</li>
              <li>Licensed drivers, flight tracking &amp; meet &amp; greet</li>
              <li>Saloon, MPV, 8–16 seat minibus &amp; coach options</li>
            </ul>

            <div className="ccg-route-review-strip">
              <CcgPremiumReviews pageKey="/slough-taxi" ariaLabel="Slough taxi customer reviews" />
            </div>
          </div>
        </section>

        <CcgRouteLhrStatusCompact title="Live Heathrow checks for Slough airport taxis" />

        {/* FLOATING CTA */}
        <button className="route-floating-book-btn" onClick={scrollToForm}>
          Book Slough Taxi
        </button>

        {/* SLOUGH AT A GLANCE */}
        <section className="slough-section" id="slough-areas">
          <header className="slough-section-header">
            <h2>🏙️ Slough at a Glance (SL1–SL7)</h2>
            <p>
              CheapCabGrab covers all main Slough districts, estates and business areas. Use this
              quick guide to see how we support each part of town.
            </p>
          </header>
          <div className="slough-two-col">
            <div className="slough-info-card">
              <h3>Town Centre & SL1</h3>
              <ul>
                <li>🚉 Slough Station & High Street area</li>
                <li>🏨 Hotels around Bath Road & A4</li>
                <li>🚕 Popular for Heathrow & central London transfers</li>
              </ul>
            </div>
            <div className="slough-info-card">
              <h3>Langley, Cippenham & Wexham</h3>
              <ul>
                <li>🏠 Residential areas with quick access to M4 & M25</li>
                <li>🏥 Close to Wexham Park Hospital</li>
                <li>🚐 Ideal for airport minibuses and group transfers</li>
              </ul>
            </div>
            <div className="slough-info-card">
              <h3>Trading Estate & Business Parks</h3>
              <ul>
                <li>🏢 Slough Trading Estate & Bath Road offices</li>
                <li>👔 Corporate account & regular transfer options</li>
                <li>🛫 Frequent Heathrow & London business trips</li>
              </ul>
            </div>
            <div className="slough-info-card">
              <h3>Windsor, Datchet & Surrounding SL Postcodes</h3>
              <ul>
                <li>🏰 Quick access to Windsor, Eton & Datchet</li>
                <li>🚖 Regular taxis to Legoland & Windsor Castle</li>
                <li>🚌 Minibus hire for events & tours</li>
              </ul>
            </div>
          </div>
        </section>

        {/* VEHICLES SECTION – LINK TO /fleet */}
        <section className="slough-section" id="slough-vehicles">
          <header className="slough-section-header">
            <h2>🚘 Our Slough Taxi Vehicles</h2>
            <p>
              From solo travellers to large groups, CheapCabGrab has the right vehicle for every
              Slough journey. All vehicles are licensed, insured and regularly inspected.
            </p>
          </header>
          <div className="slough-vehicle-grid">
            <article className="slough-vehicle-card">
              <h3>Saloon</h3>
              <p>Ideal for 1–3 passengers with light luggage travelling within Slough and to LHR.</p>
              <ul>
                <li>👥 Up to 3 passengers</li>
                <li>🧳 2–3 medium suitcases</li>
                <li>🛫 Common for Slough ↔ Heathrow runs</li>
              </ul>
            </article>

            <article className="slough-vehicle-card">
              <h3>Estate / MPV</h3>
              <p>
                Extra luggage space for families and longer stays – popular for Slough hotel
                transfers.
              </p>
              <ul>
                <li>👥 Up to 4 passengers</li>
                <li>🧳 Large boot for extra bags</li>
                <li>🏨 Great for Heathrow hotel runs</li>
              </ul>
            </article>

            <article className="slough-vehicle-card">
              <h3>7–9 Seater Minibus</h3>
              <p>
                Perfect for airport groups and extended families travelling together from Slough.
              </p>
              <ul>
                <li>👥 Up to 7–9 passengers</li>
                <li>🧳 Plenty of luggage space</li>
                <li>🚌 Ideal for events and tours</li>
              </ul>
            </article>

            <article className="slough-vehicle-card">
              <h3>14-Seater & Coach</h3>
              <p>
                For larger groups, corporate events and school trips from Slough and nearby towns.</p>
              <ul>
                <li>👥 14+ passengers</li>
                <li>📅 Pre-book only</li>
                <li>
                  🤝 Operated with our PSV partner RHP Minibus Hire UK Limited for coach services.
                </li>
              </ul>
            </article>
          </div>
          <p className="slough-fleet-link">
            Want full details of each vehicle? See our <Link to="/fleet">CheapCabGrab fleet guide</Link>.
          </p>
        </section>

        {/* TYPICAL FARES */}
        <section className="slough-section" id="slough-fares">
          <header className="slough-section-header">
            <h2>💷 Typical Slough Taxi Fares & Journey Times</h2>
            <p>
              These are example guide prices for standard saloon vehicles. Final fares depend on
              exact pickup point, time of day and vehicle type – your fixed price is always shown
              before you confirm your booking.
            </p>
          </header>
          <div className="slough-fares-grid">
            <article className="slough-fare-card">
              <h3>Slough → Heathrow</h3>
              <ul>
                <li>📍 6–10 miles</li>
                <li>⏱ 15–25 minutes (traffic dependent)</li>
                <li>💷 From <strong>£35–£45</strong></li>
              </ul>
            </article>
            <article className="slough-fare-card">
              <h3>Slough → Gatwick</h3>
              <ul>
                <li>📍 45–50 miles</li>
                <li>⏱ 45–75 minutes</li>
                <li>💷 From <strong>£90–£110</strong></li>
              </ul>
            </article>
            <article className="slough-fare-card">
              <h3>Slough → Luton</h3>
              <ul>
                <li>📍 30–35 miles</li>
                <li>⏱ 35–60 minutes</li>
                <li>💷 From <strong>£70–£90</strong></li>
              </ul>
            </article>
            <article className="slough-fare-card">
              <h3>Slough → Stansted</h3>
              <ul>
                <li>📍 55–65 miles</li>
                <li>⏱ 70–100 minutes</li>
                <li>💷 From <strong>£120–£150</strong></li>
              </ul>
            </article>
          </div>
          <p className="slough-fares-note">
            For live pricing, please use the quote form above or start a new search on the{' '}
            <Link to="/">CheapCabGrab homepage</Link>.
          </p>
        </section>

        {/* ATTRACTIONS NEAR SLOUGH */}
        <section className="slough-section" id="slough-attractions">
          <header className="slough-section-header">
            <h2>🏰 Attractions Near Slough</h2>
            <p>
              Slough is perfectly placed for days out at Windsor Castle, Legoland Windsor, Kew
              Gardens and London’s top sights. CheapCabGrab runs regular cabs and minibuses to these
              attractions.
            </p>
          </header>
          <div className="slough-attractions-grid">
            <article className="slough-attraction-card">
              <h3>Windsor Castle & Eton</h3>
              <ul>
                <li>📍 Around 4–5 miles from Slough</li>
                <li>⏱ 10–15 minutes</li>
                <li>💷 Typical fare: £15–£25 (saloon)</li>
              </ul>
              <p>Popular for day trips and royal events – taxis and minibuses available.</p>
            </article>
            <article className="slough-attraction-card">
              <h3>Legoland Windsor Resort</h3>
              <ul>
                <li>📍 Around 7–8 miles from Slough</li>
                <li>⏱ 15–20 minutes</li>
                <li>💷 Typical fare: £22–£32 (saloon)</li>
              </ul>
              <p>Family favourite – ideal with MPV or minibus for children and luggage.</p>
            </article>
            <article className="slough-attraction-card">
              <h3>Central London</h3>
              <ul>
                <li>📍 20–25 miles via M4</li>
                <li>⏱ 45–75 minutes (traffic)</li>
                <li>💷 Typical fare: £70–£100 (saloon)</li>
              </ul>
              <p>Perfect for theatre trips, shopping and late-night returns to Slough.</p>
            </article>
          </div>
          <p className="slough-attractions-link">
            See a full list of nearby highlights in our{' '}
            <Link to="/heathrow-attractions">Heathrow & Windsor attractions guide</Link>.
          </p>
        </section>

        {/* HOTELS NEAR SLOUGH / HEATHROW */}
        <section className="slough-section" id="slough-hotels">
          <header className="slough-section-header">
            <h2>🏨 Hotels Near Slough & Heathrow</h2>
            <p>
              Whether you are staying in Slough, on Bath Road or next to Heathrow terminals, we run
              24/7 transfers between local hotels and all London airports.
            </p>
          </header>
          <div className="slough-hotels-grid">
            <article className="slough-hotel-card">
              <h3>Bath Road & Trading Estate Hotels</h3>
              <p>
                Ideal for business travellers working on Slough Trading Estate or Bath Road
                corridors.
              </p>
              <ul>
                <li>📍 5–15 minutes from Slough town centre</li>
                <li>💷 Typical Slough ↔ hotel fare: £10–£20</li>
                <li>🛫 Quick access to Heathrow for early flights</li>
              </ul>
            </article>
            <article className="slough-hotel-card">
              <h3>Heathrow Perimeter Hotels</h3>
              <p>
                Hotels around Bath Road, Colnbrook and Harmondsworth serving Heathrow terminals.</p>
              <ul>
                <li>📍 15–25 minutes from Slough</li>
                <li>💷 Typical fare: £25–£40 (saloon)</li>
                <li>🚐 Minibus options for family and group trips</li>
              </ul>
            </article>
          </div>
          <p className="slough-hotels-link">
            For hotel-by-hotel details, see our dedicated{' '}
            <Link to="/heathrow-hotels">Heathrow hotels taxi guide</Link>.
          </p>
        </section>

        {/* FAQ TEASER */}
        <section className="slough-section" id="slough-faq">
          <header className="slough-section-header">
            <h2>❓ Slough Taxi FAQs</h2>
            <p>
              Here are some of the common questions passengers ask about travelling between Slough,
              Heathrow and other airports.
            </p>
          </header>
          <div className="slough-faq-grid">
            <article className="slough-faq-card">
              <h3>Do you track flights for pickups?</h3>
              <p>
                Yes – for airport journeys we usually track your flight number and adjust pickup time
                where possible if your flight is delayed. Standard free waiting time is included –
                full details are shown when you book.
              </p>
            </article>
            <article className="slough-faq-card">
              <h3>Can I book a child seat?</h3>
              <p>
                Child seats can be requested in advance for most Slough journeys. Please include your
                requirements in the booking notes so we can allocate the right vehicle.
              </p>
            </article>
            <article className="slough-faq-card">
              <h3>Do you operate late at night?</h3>
              <p>
                Yes, CheapCabGrab operates 24/7 for pre-booked trips. Late-night pickups from
                Slough, Heathrow and London are available – we recommend booking in advance.
              </p>
            </article>
          </div>
          <p className="slough-faq-link">
            Need more detail? Visit our full <Link to="/faq">CheapCabGrab FAQs</Link>.
          </p>
        </section>

        {/* SUPPORT STRIP */}
        <section className="slough-support-strip" aria-label="Help with Slough taxi bookings">
          <div className="slough-support-inner">
            <div>
              <h2>📞 Need help with a Slough taxi booking?</h2>
              <p>
                If you have any questions about pickups in Slough, flight delays or special
                requirements, our support team is here to help.
              </p>
            </div>
            <div className="slough-support-actions">
              <Link to="/contact" className="slough-support-btn">
                Contact support team →
              </Link>
              <a href="mailto:support@cheapcabgrab.com" className="slough-support-email">
                support@cheapcabgrab.com
              </a>
            </div>
          </div>
        </section>

        {/* BOTTOM CTA BAR */}
        <div className="route-bottom-cta">
          <button
            className="route-bottom-cta-btn"
            onClick={scrollToForm}
          >
            Book Slough Taxi
          </button>
        </div>
      </main>

      <FloatingSupportChat
        brand="ccg"
        defaultFlow="enquiry"
        position="right"
        showAfterMs={9000}
        nudgeText="Need Slough taxi help?"
      />
    </div>
  );
};

export default SloughTaxi;
